import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Undo2, Upload, X } from "lucide-react";
import { sanitizeInput } from "../utils/sanitize";
import PageTitle from "../components/common/PageTitle";
import LoadingOverlay from "../components/common/LoadingOverlay";
import FilterDropdown from "../components/common/FilterDropdown";
import FormInput from "../components/forms/FormInput";
import FormTextArea from "../components/forms/FormTextArea";
import {
  getCategories,
  createLesson,
  updateLesson,
  getLessonById,
} from "../services/lessonService";
import { showError, showSuccess } from "../utils/toast";
import { validateFields } from "../utils/formValidation";
import { showConfirm } from "../utils/confirm";
import { compressImage } from "../utils/imageCompression";
import { ROUTES, JWT, ROLES } from "../config/constants";
import { useCurrentUser } from "../hooks/useCurrentUser";
import {
  STORAGE_KEYS,
  CACHE_DURATION,
  saveToSessionStorage,
  getFromSessionStorage,
  invalidateCache,
} from "../utils/sessionStorage";
import { API_BASE_URL } from "../api/apiClient";

export default function LessonForm() {
  const navigate = useNavigate();
  const { id } = useParams(); // Jika ada id, berarti edit mode
  const isEditMode = !!id;
const { role } = useCurrentUser();
const isProjectAdmin =
  role?.toLowerCase() === ROLES.PROJECT_ADMIN?.toLowerCase();

  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [projects, setProjects] = useState([]);

  // Form fields
  const [projectId, setProjectId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [caseTitle, setCaseTitle] = useState("");
  const [causes, setCauses] = useState("");
  const [effect, setEffect] = useState("");
  const [projectAction, setProjectAction] = useState("");
  const [futureRecommendation, setFutureRecommendation] = useState("");

  // Photos
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [deletedPhotos, setDeletedPhotos] = useState([]); // Track deleted photos
  const [photoPreview, setPhotoPreview] = useState([]);

  // Photo viewer modal
  const [viewPhotoUrl, setViewPhotoUrl] = useState(null);
  const [showPhotoModal, setShowPhotoModal] = useState(false);

  useEffect(() => {
    // Auto-fill project from logged-in user
    const userProjectId = localStorage.getItem(JWT.PROJECT_ID_KEY) || "";
    if (userProjectId) {
      setProjectId(userProjectId);
    }

    loadCategories();
    if (isEditMode) {
      loadLesson();
    }
  }, [id]);

  useEffect(() => {
  if (isProjectAdmin) {
    loadProjects();
  }
}, [isProjectAdmin]);



async function loadProjects() {
  try {
    const res = await fetch(`${API_BASE_URL}/api/get-project-names`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem(JWT.TOKEN_KEY)}`
      }
    });

    const data = await res.json();

    setProjects(Array.isArray(data) ? data : []);
  } catch (err) {
    console.error("Failed load projects", err);
  }
}

  async function loadCategories() {
    // Try to get from cache first
    const cachedCategories = getFromSessionStorage(
      STORAGE_KEYS.CATEGORIES,
      CACHE_DURATION.CATEGORIES,
    );

    if (cachedCategories) {
      setCategories(cachedCategories);
      return;
    }

    try {
      const data = await getCategories();
      setCategories(data);
      // Save to cache
      saveToSessionStorage(STORAGE_KEYS.CATEGORIES, data);
    } catch (err) {
      console.error("Failed to load categories:", err);
      showError("Gagal memuat kategori");
    }
  }

  async function loadLesson() {
    if (!id) return;

    setLoading(true);
    try {
      const data = await getLessonById(id);
      const lesson = data.lesson || data;

      // Populate form dengan data existing
      setCategoryId(lesson.category_id || "");
      setCaseTitle(lesson.case || ""); // Backend returns 'case' not 'case_title'
      setCauses(lesson.causes || "");
      setEffect(lesson.effect || "");
      setProjectAction(lesson.project_action || "");
      setFutureRecommendation(lesson.future_recommendation || "");
      setExistingPhotos(lesson.photos || []);
    } catch (err) {
      console.error("Failed to load lesson:", err);
      showError("Gagal memuat data lesson");
      navigate(ROUTES.LESSONS);
    } finally {
      setLoading(false);
    }
  }

  function handlePhotoChange(e) {
    const files = Array.from(e.target.files || []);

    // Validate file types
    const validFiles = files.filter((file) => {
      const isValid = file.type.startsWith("image/");
      if (!isValid) {
        showError(`File ${file.name} bukan gambar yang valid`);
      }
      return isValid;
    });

    // Validate file sizes (max 5MB)
    const oversizedFiles = validFiles.filter(
      (file) => file.size > 5 * 1024 * 1024,
    );
    if (oversizedFiles.length > 0) {
      showError(`File ${oversizedFiles[0].name} melebihi ukuran maksimal 5MB`);
      return;
    }

    setSelectedPhotos((prevPhotos) => [...prevPhotos, ...validFiles]);

    // Create preview URLs
    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview((prev) => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });

    // Reset input
    e.target.value = "";
  }

  function removePhoto(index) {
    setSelectedPhotos((prev) => prev.filter((_, i) => i !== index));
    setPhotoPreview((prev) => prev.filter((_, i) => i !== index));
  }

  function handleViewPhoto(photoUrl) {
    setViewPhotoUrl(photoUrl);
    setShowPhotoModal(true);
  }

  function handleDeleteExistingPhoto(index) {
    const photoToDelete = existingPhotos[index];
    setDeletedPhotos((prev) => [...prev, photoToDelete]);
    setExistingPhotos((prev) => prev.filter((_, i) => i !== index));
    setIsDirty(true);
  }

  async function saveLesson() {
    if (!validateForm()) return false;

    setLoading(true);
    try {
const lessonData = {
  category_id: categoryId,
  case: sanitizeInput(caseTitle),
  causes: sanitizeInput(causes),
  effect: sanitizeInput(effect),
  project_action: sanitizeInput(projectAction),
  future_recommendation: sanitizeInput(futureRecommendation),
  ...(isProjectAdmin && { project_id: projectId }),
};

      // Compress photos before upload
      const compressedPhotos = await Promise.all(
        selectedPhotos.map((photo) => compressImage(photo, { maxSizeMB: 1 })),
      );

      if (isEditMode) {
        await updateLesson(id, lessonData, compressedPhotos, deletedPhotos);
        showSuccess("Lesson berhasil diupdate");
      } else {
        await createLesson(lessonData, compressedPhotos);
        showSuccess("Lesson berhasil dibuat");
      }

      return true;
    } catch (err) {
      console.error("Failed to save lesson:", err);

      if (err.response?.status === 400) {
        showError(err.response.data?.error || "Data tidak valid");
      } else if (err.response?.status === 403) {
        showError("Anda tidak memiliki akses untuk melakukan aksi ini");
      } else if (err.response?.status === 429) {
        showError("Terlalu banyak request. Tunggu sebentar dan coba lagi");
      } else {
        showError(`Gagal ${isEditMode ? "update" : "membuat"} lesson`);
      }
      return false;
    } finally {
      setLoading(false);
    }
  }

  function handleCancel() {
    if (isDirty || selectedPhotos.length > 0 || deletedPhotos.length > 0) {
      showConfirm({
        title: "Simpan Perubahan?",
        message:
          "Anda memiliki perubahan yang belum disimpan. Apakah anda ingin menyimpan?",
        confirmLabel: "Ya, Simpan",
        cancelLabel: "Abaikan",
        confirmColor: "blue",
        onConfirm: async () => {
          const saved = await saveLesson();
          if (saved) {
            navigate(ROUTES.LESSONS);
          }
        },
        onCancel: () => {
          navigate(ROUTES.LESSONS);
        },
      });
    } else {
      navigate(ROUTES.LESSONS);
    }
  }

  function validateForm() {
    const rules = [
      { value: projectId, message: "Pilih project", show: isProjectAdmin },
      { value: categoryId, message: "Pilih kategori" },
      { value: caseTitle, message: "Case title wajib diisi" },
      { value: causes, message: "Penyebab wajib diisi" },
      { value: effect, message: "Dampak wajib diisi" },
      { value: projectAction, message: "Tindakan project wajib diisi" },
      { value: futureRecommendation, message: "Rekomendasi wajib diisi" },
    ];

    // For create mode, at least one photo is required
    // if (!isEditMode) {
    //   rules.push({ value: selectedPhotos, message: "Minimal 1 foto wajib diupload", type: "minLength" });
    // }

    return validateFields(rules);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const saved = await saveLesson();
    if (saved) {
      invalidateCache([STORAGE_KEYS.LESSONS]);
      navigate(ROUTES.LESSONS);
    }
  }

  return (
    <div className="container mx-auto px-4 py-6 pt-20">
      <LoadingOverlay isLoading={loading} />

      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(ROUTES.LESSONS)}
          className="flex items-center text-sm gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <Undo2 size={14} />
          <span>Kembali ke List Lessons</span>
        </button>

        <PageTitle
          title={isEditMode ? "Edit Lesson" : "Tambah Lesson Baru"}
          subtitle={
            isEditMode
              ? "Update informasi lesson dan tambahkan foto baru"
              : "Dokumentasikan pembelajaran dari pengalaman proyek"
          }
        />
      </div>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
      >

        {/* Project */}
        {isProjectAdmin && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Project <span className="text-red-500">*</span>
            </label>
            <FilterDropdown
              options={[
                { value: "", label: "Pilih Project" },
                ...projects.map((p) => ({
                  value: p.id,
                  label: p.name,
                })),
              ]}
              value={projectId}
              onChange={(value) => {
                setProjectId(value);
                setIsDirty(true);
              }}
              placeholder="Pilih Project"
            />
          </div>
        )}


        {/* Category */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Kategori <span className="text-red-500">*</span>
          </label>
          <FilterDropdown
            options={[
              { value: "", label: "Pilih Kategori" },
              ...categories.map((cat) => ({
                value: cat.id,
                label: cat.name,
              })),
            ]}
            value={categoryId}
            onChange={(value) => {
              setCategoryId(value);
              setIsDirty(true);
            }}
            placeholder="Pilih Kategori"
          />
        </div>


        {/* Case Title */}
        <FormInput
          label="Case / Judul Kasus"
          value={caseTitle}
          onChange={(val) => { setCaseTitle(val); setIsDirty(true); }}
          placeholder="Contoh: Beton Retak saat Pengecoran Siang Hari"
          maxLength={500}
          hint="Maksimal 500 karakter."
        />

        {/* Causes */}
        <FormTextArea
          label="Penyebab / Root Cause"
          value={causes}
          onChange={(val) => { setCauses(val); setIsDirty(true); }}
          placeholder="Jelaskan akar penyebab masalah..."
          maxLength={1000}
          hint="Maksimal 1000 karakter"
        />

        {/* Effect */}
        <FormTextArea
          label="Dampak / Impact"
          value={effect}
          onChange={(val) => { setEffect(val); setIsDirty(true); }}
          placeholder="Jelaskan dampak ke project..."
          maxLength={1000}
        />

        {/* Project Action */}
        <FormTextArea
          label="Tindakan yang Dilakukan"
          value={projectAction}
          onChange={(val) => { setProjectAction(val); setIsDirty(true); }}
          placeholder="Jelaskan tindakan yang sudah dilakukan..."
          maxLength={1000}
        />

        {/* Future Recommendation */}
        <FormTextArea
          label="Rekomendasi untuk Masa Depan"
          value={futureRecommendation}
          onChange={(val) => { setFutureRecommendation(val); setIsDirty(true); }}
          placeholder="Rekomendasi agar masalah tidak terulang..."
          maxLength={1000}
        />

        {/* Existing Photos (Edit mode only) */}
        {isEditMode && existingPhotos.length > 0 && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Foto yang Sudah Ada
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {existingPhotos.map((photo, index) => (
                <div key={index} className="flex flex-col gap-2">
                  <div
                    className="relative group cursor-pointer"
                    onClick={() => handleViewPhoto(`${API_BASE_URL}/${photo}`)}
                  >
                    <img
                      src={`${API_BASE_URL}/${photo}`}
                      alt={`Existing ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg border border-gray-300"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-40 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white text-sm">
                        Klik untuk lihat
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeleteExistingPhoto(index)}
                    className="w-full px-3 py-1.5 text-xs bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  >
                    Hapus Foto
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Photo Upload */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tambah Foto (Opsional)
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotoChange}
              className="hidden"
              id="photo-upload"
            />
            <label
              htmlFor="photo-upload"
              className="cursor-pointer flex flex-col items-center"
            >
              <Upload size={48} className="text-gray-400 mb-2" />
              <span className="text-sm text-gray-600">
                Klik untuk upload foto atau drag & drop
              </span>
              <span className="text-xs text-gray-500 mt-1">
                JPG, JPEG, PNG (Max 5MB per file)
              </span>
            </label>
          </div>

          {/* Photo Preview */}
          {photoPreview.length > 0 && (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
              {photoPreview.map((preview, index) => (
                <div key={index} className="relative group">
                  <img
                    src={preview}
                    alt={`Preview ${index + 1}`}
                    className="w-full h-32 object-cover rounded-lg border border-gray-300"
                  />
                  <button
                    type="button"
                    onClick={() => removePhoto(index)}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={16} />
                  </button>
                  <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                    {selectedPhotos[index]?.name}
                  </div>
                </div>
              ))}
            </div>
          )}

          <p className="mt-2 text-xs text-gray-500">
            {isEditMode
              ? "Tambahkan foto baru untuk melengkapi lesson ini. Foto lama tidak akan terhapus."
              : "Upload foto jika tersedia. Anda bisa upload beberapa foto sekaligus."}
          </p>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleCancel}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Batal
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading
              ? "Menyimpan..."
              : isEditMode
                ? "Update Lesson"
                : "Simpan Lesson"}
          </button>
        </div>
      </form>

      {/* Photo Modal */}
      {showPhotoModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setShowPhotoModal(false)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <img
              src={viewPhotoUrl}
              alt="Enlarged"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
            <button
              onClick={() => setShowPhotoModal(false)}
              className="absolute text-sm top-4 right-4 bg-white/90 text-gray-900 rounded-full px-3 py-1.5 hover:bg-gray-100"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
